// ─────────────────────────────────────────────────────────────────────────────
// DownloadPackageModal — opened by the PACKAGE cell on a node row. Offers the two
// artifacts a host needs to run Node Core, as two separate downloads:
//
//   Binary  GET  /enterprise-nodes/:id/binary-url  → presigned S3 URL, read-only
//   Config  POST /enterprise-nodes/:id/config      → node-core.json, a WRITE
//
// They are two buttons rather than one because they are genuinely independent and
// fail for unrelated reasons: the binary can be unavailable because of a server
// IAM problem, while the config is refused (409) whenever the node is ONLINE,
// since an enrollment token can overwrite a running node's identity. Either can
// work while the other doesn't, so neither is allowed to block the other.
//
// This was UI-only until the download endpoints landed; the package descriptor
// used to come from a hardcoded mock (../../agentPackage, now deleted).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./rowActions.module.css";
import { CloseIcon, DownloadIcon } from "./icons";
import { dash, nodeLabel } from "../../../format";
import useNodeCoreDownload from "../../../hooks/useNodeCoreDownload";
import { formatBytes, startCommand } from "../../nodeCoreDownload";

export default function DownloadPackageModal({ node, onClose }) {
  const {
    loadBinaryMeta, getBinary, binaryLoading, binaryError, binaryMeta,
    getConfig, configLoading, configError, configResult,
  } = useNodeCoreDownload(node?.node_id);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Read-only, so it costs nothing to show the real size and platform up front
  // instead of making the operator click to find out.
  useEffect(() => { loadBinaryMeta(); }, [loadBinaryMeta]);

  const label = dash(nodeLabel(node));
  const ip = dash(node.ip);

  const rows = {
    Target:   `${ip} (${label})`,
    Platform: binaryMeta?.platform ?? "linux",
    Size:     dash(formatBytes(binaryMeta?.size_bytes)),
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-package-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>

        <h2 className={styles.title} id="download-package-title">Download Node Core</h2>
        <p className={styles.subtitle}>
          Node <span className={styles.nodeName}>{label}</span> · {ip}
        </p>

        <dl className={styles.summary}>
          {Object.entries(rows).map(([key, value]) => (
            <div key={key} className={styles.row}>
              <dt className={styles.key}>{key}</dt>
              <dd className={styles.value}>{value}</dd>
            </div>
          ))}
        </dl>

        <p className={styles.subtitle}>
          Put both files on the host, then run{" "}
          <code>{startCommand(configResult?.filename)}</code>
        </p>

        {/* Per-artifact errors. Kept next to their own button so it is obvious
            which of the two failed — they fail for unrelated reasons. */}
        {binaryError && <p className={styles.warnText}>{binaryError}</p>}
        {configError && <p className={styles.warnText}>{configError}</p>}

        {/* A download can be suppressed by the browser with no error of any kind
            — an ad/popup blocker intercepting it, or a click whose user
            activation had lapsed. Neither raises anything we can catch, so the
            only honest remedy is a real link the operator can click themselves. */}
        {binaryMeta?.binary_url && (
          <p className={styles.subtitle}>
            Download didn&apos;t start?{" "}
            <a href={binaryMeta.binary_url} download="node-core" rel="noopener">
              Use this direct link
            </a>{" "}
            (valid for {Math.max(0, Math.round((binaryMeta.expires_in_seconds ?? 0) / 60))} min).
          </p>
        )}

        {/* The config carries a live, single-use enrollment token. Say so once the
            file has actually been written, not before. */}
        {configResult && (
          <p className={styles.subtitle}>
            Saved <code>{configResult.filename}</code>. It contains this node&apos;s
            enrollment token
            {configResult.token_reused ? " (the one already issued)" : " (newly issued)"}
            {configResult.expires_at
              ? ` — the node must enroll before ${new Date(configResult.expires_at).toLocaleString()}.`
              : "."}
          </p>
        )}
        {configResult?.warnings?.map((w) => (
          <p key={w} className={styles.warnText}>{w}</p>
        ))}

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={getConfig}
            disabled={configLoading}
          >
            <DownloadIcon /> {configLoading ? "Generating…" : "Download Config"}
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={getBinary}
            disabled={binaryLoading}
          >
            <DownloadIcon /> {binaryLoading ? "Preparing…" : "Download Binary"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
